import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy — E Broadway Blvd", address: "4707 E Broadway Blvd, Tucson, AZ 85711", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — N Oracle Rd", address: "4625 N Oracle Rd, Tucson, AZ 85705", hours: "Call for hours" },
  { name: "FedEx Office — N Campbell Ave", address: "1702 N Campbell Ave, Tucson, AZ 85719", hours: "Call for hours" },
  { name: "The UPS Store — E Speedway Blvd", address: "1800 E Speedway Blvd, Tucson, AZ 85719", hours: "Call for hours" },
  { name: "CVS Pharmacy — E Tanque Verde Rd", address: "7110 E Tanque Verde Rd, Tucson, AZ 85715", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — W Ina Rd (Marana)", address: "3770 W Ina Rd, Tucson, AZ 85741", hours: "Call for hours" },
];

export default function TucsonHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 4);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Tucson Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Tucson and Southern Arizona resources to help you gather everything you need before your next international trip.
          </p>
        </div>

        <div className="space-y-6">
          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Tucson</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Tucson include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button onClick={() => setPhotoExpanded(true)} className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors">
                  <i className="ri-add-circle-line text-sm"></i>Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. UA students applying for their first passport should confirm photo compliance before visiting the acceptance clerk.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Tucson MVD — Arizona Motor Vehicle Division</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Arizona driver&apos;s license or REAL ID, the Tucson MVD handles these services.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  { name: "Tucson MVD — E 22nd Street", address: "5975 E 22nd St, Tucson, AZ 85711", hours: "Mon–Fri 8:00 AM – 4:30 PM", note: "Primary Tucson MVD location. Appointment strongly recommended — walk-in wait times can be substantial.", featured: true },
                  { name: "Tucson MVD — N Stone Ave", address: "400 W Congress St, Tucson, AZ 85701", hours: "Mon–Fri 8:00 AM – 4:30 PM", note: "Downtown Tucson MVD. Convenient for UA area and central Tucson residents.", featured: false },
                ].map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                    <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://servicearizona.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>ServiceArizona.com — AZ MVD
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online at ServiceArizona.com before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airport */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airport</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airport Serving Tucson Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  { code: "TUS", name: "Tucson International Airport", distance: "South Tucson — about 8 miles from downtown", address: "7250 S Tucson Blvd, Tucson, AZ 85756", note: "Primary commercial airport for Tucson travelers. Service to major hubs including PHX, DFW, LAX, DEN, and ATL with international connections. Davis-Monthan families and Raytheon travelers frequently fly TUS.", featured: true },
                  { code: "PHX", name: "Phoenix Sky Harbor (alternative)", distance: "~120 miles north via I-10", address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034", note: "Many Tucson travelers use PHX for broader nonstop international options, especially to Mexico, Europe, and Asia. Factor in the 2-hour drive.", featured: false },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed">{airport.note}</p>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing will not work — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}